'use client';

import { useState, useMemo } from 'react';
import { ChevronUpIcon, ChevronDownIcon, ArrowDownTrayIcon, MagnifyingGlassIcon, XMarkIcon, InformationCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { ESMOverdueStockItem } from '@/types/esmOverdueStocks';
import StockActionIcons from '@/components/stocks/StockActionIcons';
import ReasoningFormatter from '@/components/gsm-stock/ReasoningFormatter';
import ESMStockDetailModal from '@/components/esm-stock/ESMStockDetailModal';

interface ESMOverdueStocksTableProps {
  stocks: ESMOverdueStockItem[];
}

type SortField = 'stock_name' | 'current_price' | 'market_capitalization' | 'price_to_earning' | 'esm2_entry_date';
type SortDirection = 'asc' | 'desc';

export default function ESMOverdueStocksTable({ stocks }: ESMOverdueStocksTableProps) {
  const [sortField, setSortField] = useState<SortField>('esm2_entry_date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStock, setSelectedStock] = useState<ESMOverdueStockItem | null>(null);
  const [selectedStockIsin, setSelectedStockIsin] = useState<string | null>(null);

  const handleSort = (field: SortField) => {
    const dir = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(dir);
  };

  const handleStockClick = (isin: string) => {
    setSelectedStockIsin(isin);
  };

  const handleCloseModal = () => {
    setSelectedStockIsin(null);
  };

  // Filter and sort stocks
  const filteredAndSortedStocks = useMemo(() => {
    // Start with all stocks
    let filtered = stocks;
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(stock => 
        (stock.stock_name && stock.stock_name.toLowerCase().includes(query)) ||
        (stock.isin && stock.isin.toLowerCase().includes(query)) ||
        (stock.nse_code && stock.nse_code.toLowerCase().includes(query)) ||
        (stock.bse_code && stock.bse_code.toLowerCase().includes(query)) ||
        (stock.stock_code && stock.stock_code.toLowerCase().includes(query))
      );
    }

    // Apply sorting
    const copy = [...filtered];
    return copy.sort((a, b) => {
      let av: unknown, bv: unknown;
      
      if (sortField === 'esm2_entry_date') {
        av = a.esm2_entry_date;
        bv = b.esm2_entry_date;
      } else {
        av = a[sortField as keyof ESMOverdueStockItem];
        bv = b[sortField as keyof ESMOverdueStockItem];
      }
      
      const m = sortDirection === 'asc' ? 1 : -1;
      
      if (av == null && bv == null) return 0;
      if (av == null) return 1 * m;
      if (bv == null) return -1 * m;
      
      if (sortField === 'esm2_entry_date') {
        const ad = new Date(av as string).getTime();
        const bd = new Date(bv as string).getTime();
        if (isNaN(ad) && isNaN(bd)) return 0;
        if (isNaN(ad)) return 1 * m;
        if (isNaN(bd)) return -1 * m;
        return (ad - bd) * m;
      }
      
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * m;
      if (typeof av === 'string' && typeof bv === 'string') return av.localeCompare(bv) * m;
      return av < bv ? -1 * m : av > bv ? 1 * m : 0;
    });
  }, [stocks, sortField, sortDirection, searchQuery]);

  const formatCurrency = (value: number | null | undefined): string => {
    if (typeof value !== 'number' || isNaN(value)) return 'N/A';
    return `₹${value.toFixed(2)}`;
  };

  const formatMarketCap = (value: number | null | undefined): string => {
    if (typeof value !== 'number' || isNaN(value)) return 'N/A';
    const isNegative = value < 0;
    const absValue = Math.abs(value);
    const sign = isNegative ? '-' : '';
    
    if (absValue >= 1000) return `${sign}₹${(absValue / 1000).toFixed(2)}K Cr`;
    if (absValue >= 1) return `${sign}₹${absValue.toFixed(2)} Cr`;
    return `${sign}₹${(absValue * 100).toFixed(2)} L`;
  };

  const formatRatio = (value: number | null | undefined): string => {
    if (typeof value !== 'number' || isNaN(value)) return 'N/A';
    return value.toFixed(2);
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getOverdueDuration = (entryDate: string): number => {
    try {
      const entry = new Date(entryDate);
      const today = new Date();
      const diffTime = Math.abs(today.getTime() - entry.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch {
      return 0;
    }
  };

  const getOverdueDurationColor = (days: number): string => {
    if (days <= 100) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    if (days <= 200) return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
    return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
  };

  const exportToCSV = () => {
    const headers = [
      'ISIN', 'Stock Name', 'NSE Code', 'BSE Code', 'Stock Code',
      'Current Price', 'Market Cap (Cr)', 'P/E Ratio', 'ESM2 Entry Date',
      'Days in ESM2', 'TradingView Link', 'Screener Link', 'External Code'
    ];
    
    const csvData = filteredAndSortedStocks.map(stock => [
      stock.isin,
      `"${stock.stock_name?.replace(/"/g, '""')}"`,
      stock.nse_code || '',
      stock.bse_code || '',
      stock.stock_code || '',
      stock.current_price || 'N/A',
      stock.market_capitalization || 'N/A',
      stock.price_to_earning || 'N/A',
      stock.esm2_entry_date,
      getOverdueDuration(stock.esm2_entry_date),
      stock.trading_view_navlink || 'N/A',
      stock.screener_navlink || 'N/A',
      stock.stock_code ? `"${stock.stock_code},"` : ''
    ]);

    const csvContent = [headers.join(','), ...csvData.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `esm_overdue_stocks_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ChevronUpIcon className="w-4 h-4 text-zinc-400" />;
    }
    return sortDirection === 'asc' ? 
      <ChevronUpIcon className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /> : 
      <ChevronDownIcon className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />;
  };

  if (!stocks.length) {
    return (
      <div className="text-center py-12 bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800">
        <p className="text-zinc-500 dark:text-zinc-400 text-lg">No ESM overdue stocks found</p>
        <p className="text-zinc-400 dark:text-zinc-500 text-sm mt-2">Try adjusting your search criteria</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 shadow-sm overflow-hidden table-container-with-popups">
      {/* Header with Search and Export Button */}
      <div className="px-6 py-4 border-b border-zinc-200 dark:border-zinc-800">
        <div className="flex flex-col gap-4">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-zinc-900 dark:text-white flex items-center gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                ESM Stage 2 Overdue Stocks ({filteredAndSortedStocks.length}{filteredAndSortedStocks.length !== stocks.length ? ` of ${stocks.length}` : ''})
              </h3>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
                Stocks that have remained in ESM Stage 2 beyond expected exit timeframes
              </p>
            </div>
            
            <button
              onClick={exportToCSV}
              className="inline-flex items-center px-4 py-2 border border-zinc-300 dark:border-zinc-700 rounded-lg text-sm font-semibold text-zinc-700 dark:text-zinc-200 bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 transition-colors"
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
              Export CSV
            </button>
          </div>

          {/* Search Row */}
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search Input */}
            <div className="relative flex-1 max-w-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-zinc-400" />
              </div>
              <input
                type="text"
                placeholder="Search stocks, ISIN, codes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-10 pr-10 py-2 text-sm rounded-lg border border-zinc-300 bg-white text-zinc-900 placeholder:text-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <XMarkIcon className="h-4 w-4 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="table-wrapper-popup-safe min-table-height">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-zinc-200 dark:divide-zinc-800">
            <thead className="bg-zinc-50 dark:bg-zinc-800/60">
              <tr>
                <th 
                  onClick={() => handleSort('stock_name')}
                  className="px-4 py-3 text-left text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                >
                  <div className="flex items-center gap-1">
                    Stock Details
                    <SortIcon field="stock_name" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('market_capitalization')}
                  className="px-4 py-3 text-right text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                >
                  <div className="flex items-center justify-end gap-1">
                    Market Cap
                    <SortIcon field="market_capitalization" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('price_to_earning')}
                  className="px-4 py-3 text-right text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider cursor-pointer hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                >
                  <div className="flex items-center justify-end gap-1">
                    P/E Ratio
                    <SortIcon field="price_to_earning" />
                  </div>
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                  Overdue Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-zinc-900 divide-y divide-zinc-200 dark:divide-zinc-800">
              {filteredAndSortedStocks.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <MagnifyingGlassIcon className="h-12 w-12 text-zinc-400 mb-4" />
                      <p className="text-zinc-500 dark:text-zinc-400 text-lg">No stocks match your search</p>
                      <p className="text-zinc-400 dark:text-zinc-500 text-sm mt-2">
                        {searchQuery ? 'Try adjusting your search terms' : 'No overdue stocks available'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredAndSortedStocks.map((stock) => {
                  const overdueDays = getOverdueDuration(stock.esm2_entry_date);
                  return (
                    <tr 
                      key={stock.isin} 
                      className="hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors table-row-with-menu"
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex-1">
                          <StockActionIcons
                            isin={stock.isin}
                            stockName={stock.stock_name}
                            tradingViewLink={stock.trading_view_navlink}
                            screenerLink={stock.screener_navlink}
                            currentPrice={stock.current_price || undefined}
                            showNotesCount
                          />
                          <div 
                            className="text-sm font-medium text-zinc-900 dark:text-white cursor-pointer hover:text-emerald-600 dark:hover:text-emerald-400"
                            onClick={() => handleStockClick(stock.isin)}
                          >
                            {stock.stock_name}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-zinc-900 dark:text-white text-right">
                        {formatMarketCap(stock.market_capitalization)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-zinc-900 dark:text-white text-right">
                        {formatRatio(stock.price_to_earning)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <div className="space-y-2">
                          <div className="flex flex-col items-center gap-1">
                            <div className="text-xs text-zinc-600 dark:text-zinc-400">
                              Entry: {formatDate(stock.esm2_entry_date)}
                            </div>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getOverdueDurationColor(overdueDays)}`}>
                              {overdueDays} days overdue
                            </span>
                          </div>
                          <button
                            onClick={() => setSelectedStock(stock)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-500/10 text-red-700 dark:text-red-400 rounded-md hover:bg-red-500/20 transition-colors"
                            title="View overdue analysis"
                          >
                            <InformationCircleIcon className="h-3 w-3" />
                            View Analysis
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-3 bg-zinc-50 dark:bg-zinc-800/60 border-t border-zinc-200 dark:border-zinc-800">
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          Showing {filteredAndSortedStocks.length} overdue stock{filteredAndSortedStocks.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Analysis Modal */}
      {selectedStock && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 shadow-xl max-w-5xl w-full max-h-[85vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-zinc-200 dark:border-zinc-800">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-zinc-900 dark:text-white flex items-center gap-2">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                    ESM Stage 2 Overdue Analysis
                  </h3>
                  <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
                    {selectedStock.stock_name} ({selectedStock.isin})
                  </p>
                </div>
                <button
                  onClick={() => setSelectedStock(null)}
                  className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="px-6 py-6 overflow-y-auto max-h-[calc(85vh-200px)]">
              <div className="space-y-6">
                {/* Status Overview */}
                <div className="bg-zinc-50 dark:bg-zinc-800/60 p-4 rounded-xl border border-zinc-200 dark:border-zinc-800">
                  <h4 className="font-semibold text-zinc-900 dark:text-white mb-3 text-lg">Overdue Status</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="text-center">
                      <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-zinc-100 text-zinc-800 dark:bg-zinc-800 dark:text-zinc-200">
                        {formatDate(selectedStock.esm2_entry_date)}
                      </span>
                      <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1">Entry Date</p>
                    </div>
                    <div className="text-center">
                      <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${getOverdueDurationColor(getOverdueDuration(selectedStock.esm2_entry_date))}`}>
                        {getOverdueDuration(selectedStock.esm2_entry_date)} Days
                      </span>
                      <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1">Days in ESM2</p>
                    </div>
                    <div className="text-center">
                      <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                        OVERDUE
                      </span>
                      <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1">Status</p>
                    </div>
                  </div>
                </div>

                {/* Stock Details */}
                <div>
                  <h4 className="font-semibold text-zinc-900 dark:text-white mb-4 text-lg">Stock Information</h4>
                  <div className="bg-zinc-50 dark:bg-zinc-800/60 rounded-xl p-4">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-white dark:bg-zinc-900 p-3 rounded-lg border border-zinc-200 dark:border-zinc-800">
                        <div className="text-sm text-zinc-500 dark:text-zinc-400">Current Price</div>
                        <div className="text-lg font-semibold text-zinc-900 dark:text-white">{formatCurrency(selectedStock.current_price)}</div>
                      </div>
                      <div className="bg-white dark:bg-zinc-900 p-3 rounded-lg border border-zinc-200 dark:border-zinc-800">
                        <div className="text-sm text-zinc-500 dark:text-zinc-400">Market Cap</div>
                        <div className="text-lg font-semibold text-zinc-900 dark:text-white">{formatMarketCap(selectedStock.market_capitalization)}</div>
                      </div>
                      <div className="bg-white dark:bg-zinc-900 p-3 rounded-lg border border-zinc-200 dark:border-zinc-800">
                        <div className="text-sm text-zinc-500 dark:text-zinc-400">P/E Ratio</div>
                        <div className="text-lg font-semibold text-zinc-900 dark:text-white">{formatRatio(selectedStock.price_to_earning)}</div>
                      </div>
                      <div className="bg-white dark:bg-zinc-900 p-3 rounded-lg border border-zinc-200 dark:border-zinc-800">
                        <div className="text-sm text-zinc-500 dark:text-zinc-400">Industry</div>
                        <div className="text-lg font-semibold text-zinc-900 dark:text-white">{selectedStock.industry_name}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Detailed Analysis */}
                <div>
                  <h4 className="font-semibold text-zinc-900 dark:text-white mb-4 text-lg flex items-center">
                    <InformationCircleIcon className="h-5 w-5 mr-2 text-red-500" />
                    Detailed Overdue Analysis
                  </h4>
                  <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-6">
                    <ReasoningFormatter 
                      text={selectedStock.prediction.reasoning}
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/60 flex justify-end">
              <button
                onClick={() => setSelectedStock(null)}
                className="px-6 py-2 text-sm font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ESM Stock Detail Modal */}
      {selectedStockIsin && (
        <ESMStockDetailModal 
          isin={selectedStockIsin} 
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}
